import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { FraudWarning } from '../components/FraudDisclaimer';
import { toast } from 'sonner';
import { Send, ArrowLeft, MessageSquare, Car, Wifi, WifiOff, HandCoins, CheckCircle2, XCircle, ArrowLeftRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getWsUrl() {
  const base = BACKEND_URL.replace(/^http/, 'ws');
  return `${base}/api/ws/chat`;
}

export default function Messages() {
  const { convId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [activeConv, setActiveConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerForm, setOfferForm] = useState({ amount: '', note: '' });
  const [submittingOffer, setSubmittingOffer] = useState(false);
  const [counterFor, setCounterFor] = useState(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  useEffect(() => {
    API.get('/conversations').then(r => {
      setConversations(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // WebSocket connection
  const connectWs = useCallback((cId) => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    const token = localStorage.getItem('auth_token');
    if (!token || !cId) return;

    const wsUrl = `${getWsUrl()}/${cId}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setWsConnected(true);
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        setMessages(prev => {
          if (prev.some(m => m.message_id === msg.message_id)) return prev;
          return [...prev, msg];
        });
        // Update conversation last message
        setConversations(prev => prev.map(c =>
          c.conversation_id === cId ? { ...c, last_message: msg.content, last_message_at: msg.created_at } : c
        ));
      } catch {}
    };

    ws.onclose = () => {
      setWsConnected(false);
      // Reconnect after 3 seconds
      reconnectRef.current = setTimeout(() => {
        if (wsRef.current === ws) connectWs(cId);
      }, 3000);
    };

    ws.onerror = () => {
      setWsConnected(false);
    };

    wsRef.current = ws;
  }, []);

  // Load messages and connect WebSocket when conversation changes
  useEffect(() => {
    if (convId) {
      const conv = conversations.find(c => c.conversation_id === convId);
      setActiveConv(conv || null);

      API.get(`/conversations/${convId}/messages`).then(r => {
        setMessages(r.data);
      }).catch(() => {});

      connectWs(convId);

      return () => {
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        if (reconnectRef.current) {
          clearTimeout(reconnectRef.current);
        }
      };
    }
  }, [convId, conversations, connectWs]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-open offer modal if URL has ?action=offer (from listing sticky bar)
  useEffect(() => {
    if (convId && searchParams.get('action') === 'offer') {
      setShowOfferModal(true);
      setCounterFor(null);
    }
  }, [convId, searchParams]);

  const submitOffer = async (e) => {
    if (e) e.preventDefault();
    const amount = parseFloat(offerForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid offer amount');
      return;
    }
    setSubmittingOffer(true);
    try {
      if (counterFor) {
        const res = await API.post(`/conversations/${convId}/offer/${counterFor.message_id}/respond`, {
          action: 'counter',
          counter_amount: amount,
          note: offerForm.note || '',
        });
        // Add counter message locally (in case WS delivery is slow)
        setMessages(prev => prev.some(m => m.message_id === res.data.response_message.message_id) ? prev : [...prev, res.data.response_message]);
        // Also mark the original offer as countered
        setMessages(prev => prev.map(m => m.message_id === counterFor.message_id ? { ...m, offer_status: 'countered' } : m));
        toast.success('Counter offer sent');
      } else {
        const res = await API.post(`/conversations/${convId}/offer`, {
          amount,
          note: offerForm.note || '',
        });
        setMessages(prev => prev.some(m => m.message_id === res.data.message_id) ? prev : [...prev, res.data]);
        toast.success('Offer sent');
      }
      setShowOfferModal(false);
      setCounterFor(null);
      setOfferForm({ amount: '', note: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send offer');
    } finally {
      setSubmittingOffer(false);
    }
  };

  const respondToOffer = async (offer, action) => {
    if (action === 'counter') {
      setCounterFor(offer);
      setOfferForm({ amount: String(offer.offer_amount || ''), note: '' });
      setShowOfferModal(true);
      return;
    }
    try {
      const res = await API.post(`/conversations/${convId}/offer/${offer.message_id}/respond`, { action });
      setMessages(prev => prev.map(m => m.message_id === offer.message_id ? { ...m, offer_status: action === 'accept' ? 'accepted' : 'rejected' } : m));
      setMessages(prev => prev.some(m => m.message_id === res.data.response_message.message_id) ? prev : [...prev, res.data.response_message]);
      toast.success(action === 'accept' ? 'Offer accepted' : 'Offer rejected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to respond');
    }
  };

  const formatGHS = (n) => `GHS ${new Intl.NumberFormat('en-GH').format(Math.round(Number(n) || 0))}`;

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !convId) return;
    const content = newMsg.trim();
    setNewMsg('');
    setSending(true);

    // Try WebSocket first
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ content }));
      setSending(false);
    } else {
      // Fallback to REST API
      try {
        await API.post(`/conversations/${convId}/messages`, { content });
        const res = await API.get(`/conversations/${convId}/messages`);
        setMessages(res.data);
      } catch {}
      setSending(false);
    }
  };

  const otherName = (conv) => {
    if (!conv?.participant_names || !user) return 'User';
    const otherId = conv.participants?.find(p => p !== user.user_id);
    return conv.participant_names[otherId] || 'User';
  };

  return (
    <main data-testid="messages-page" className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="flex h-[calc(100vh-200px)] bg-card border border-border/50 rounded-xl overflow-hidden">
          {/* Sidebar */}
          <div className={`w-full md:w-80 border-r border-border/50 flex-shrink-0 flex flex-col ${convId ? 'hidden md:flex' : 'flex'}`} data-testid="conv-sidebar">
            <div className="p-4 border-b border-border/50">
              <h2 className="font-semibold">Messages</h2>
            </div>
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="p-4 text-sm text-muted-foreground text-center">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No conversations yet</p>
                </div>
              ) : (
                <div>
                  {conversations.map(c => (
                    <button key={c.conversation_id} onClick={() => navigate(`/messages/${c.conversation_id}`)}
                      className={`w-full text-left p-4 border-b border-border/50 hover:bg-muted/50 transition-colors ${convId === c.conversation_id ? 'bg-muted/50' : ''}`}
                      data-testid={`conv-${c.conversation_id}`}>
                      <div className="flex gap-3">
                        {c.listing_photo ? (
                          <img src={c.listing_photo.startsWith('http') ? c.listing_photo : `${BACKEND_URL}${c.listing_photo}`}
                            alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <Car className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{otherName(c)}</p>
                          <p className="text-xs text-muted-foreground truncate">{c.listing_title}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message || 'No messages yet'}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col ${!convId ? 'hidden md:flex' : 'flex'}`}>
            {convId ? (
              <>
                <div className="p-4 border-b border-border/50 flex items-center gap-3">
                  <Button variant="ghost" size="icon" className="md:hidden" onClick={() => navigate('/messages')} data-testid="back-to-convs">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{activeConv ? otherName(activeConv) : 'Chat'}</p>
                    <p className="text-xs text-muted-foreground">{activeConv?.listing_title}</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs" data-testid="ws-status">
                    {wsConnected ? (
                      <><Wifi className="w-3.5 h-3.5 text-trust-green" /><span className="text-trust-green">Live</span></>
                    ) : (
                      <><WifiOff className="w-3.5 h-3.5 text-muted-foreground" /><span className="text-muted-foreground">Offline</span></>
                    )}
                  </div>
                </div>
                {/* Chat Safety Warning */}
                <div className="px-4 pt-3">
                  <FraudWarning type="chat" />
                </div>
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {messages.map(m => {
                      const isMine = m.sender_id === user?.user_id;
                      const isOffer = m.message_type === 'offer' || m.message_type === 'counter_offer';
                      const isOfferResolution = m.message_type === 'offer_accepted' || m.message_type === 'offer_rejected';

                      if (isOffer) {
                        const status = m.offer_status || 'pending';
                        const statusStyles = {
                          pending: 'border-amber-400/50 bg-amber-50 dark:bg-amber-950/30',
                          accepted: 'border-emerald-400/50 bg-emerald-50 dark:bg-emerald-950/30',
                          rejected: 'border-rose-400/50 bg-rose-50 dark:bg-rose-950/30',
                          countered: 'border-blue-400/50 bg-blue-50 dark:bg-blue-950/30',
                        }[status];
                        return (
                          <div key={m.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] sm:max-w-[75%] rounded-2xl border ${statusStyles} p-4 space-y-3`} data-testid={`offer-${m.message_id}`}>
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                                  <HandCoins className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                </div>
                                <div>
                                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                                    {m.message_type === 'counter_offer' ? 'Counter offer' : 'Offer'} · {isMine ? 'you sent' : 'received'}
                                  </p>
                                  <p className="font-mono-display font-bold text-lg leading-none">{formatGHS(m.offer_amount)}</p>
                                </div>
                                <span className={`ml-auto text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
                                  status === 'pending' ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300' :
                                  status === 'accepted' ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' :
                                  status === 'rejected' ? 'bg-rose-500/20 text-rose-700 dark:text-rose-300' :
                                  'bg-blue-500/20 text-blue-700 dark:text-blue-300'
                                }`}>{status}</span>
                              </div>
                              {m.offer_note && (
                                <p className="text-sm text-muted-foreground italic">"{m.offer_note}"</p>
                              )}
                              {status === 'pending' && !isMine && (
                                <div className="flex flex-wrap gap-2 pt-1">
                                  <Button size="sm" onClick={() => respondToOffer(m, 'accept')} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid={`offer-accept-${m.message_id}`}>
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Accept
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => respondToOffer(m, 'counter')} className="gap-1.5" data-testid={`offer-counter-${m.message_id}`}>
                                    <ArrowLeftRight className="w-3.5 h-3.5" /> Counter
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => respondToOffer(m, 'reject')} className="text-rose-600 border-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 gap-1.5" data-testid={`offer-reject-${m.message_id}`}>
                                    <XCircle className="w-3.5 h-3.5" /> Reject
                                  </Button>
                                </div>
                              )}
                              <p className="text-[10px] text-muted-foreground">
                                {new Date(m.created_at).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        );
                      }

                      if (isOfferResolution) {
                        const accepted = m.message_type === 'offer_accepted';
                        return (
                          <div key={m.message_id} className="flex justify-center">
                            <div className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
                              accepted ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
                            }`} data-testid={`offer-resolution-${m.message_id}`}>
                              {accepted ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                              {m.content}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={m.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                            isMine ? 'bg-gold text-black rounded-br-md' : 'bg-muted rounded-bl-md'
                          }`} data-testid={`msg-${m.message_id}`}>
                            <p>{m.content}</p>
                            <p className={`text-[10px] mt-1 ${isMine ? 'text-black/50' : 'text-muted-foreground'}`}>
                              {new Date(m.created_at).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                <form onSubmit={sendMessage} className="p-4 border-t border-border/50 flex gap-2 items-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setCounterFor(null); setOfferForm({ amount: '', note: '' }); setShowOfferModal(true); }}
                    className="h-11 gap-1.5 hidden sm:inline-flex"
                    data-testid="open-offer-modal-btn"
                  >
                    <HandCoins className="w-4 h-4" /> Offer
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => { setCounterFor(null); setOfferForm({ amount: '', note: '' }); setShowOfferModal(true); }}
                    className="h-11 w-11 sm:hidden"
                    data-testid="open-offer-modal-btn-mobile"
                  >
                    <HandCoins className="w-4 h-4" />
                  </Button>
                  <Input value={newMsg} onChange={(e) => setNewMsg(e.target.value)}
                    placeholder="Type a message..." className="h-11" data-testid="message-input" />
                  <Button type="submit" disabled={sending || !newMsg.trim()} className="bg-gold text-black hover:brightness-110 h-11 w-11" data-testid="send-message-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">Select a conversation to start chatting</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Offer / Counter Offer Modal */}
      <Dialog open={showOfferModal} onOpenChange={(o) => { setShowOfferModal(o); if (!o) setCounterFor(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandCoins className="w-5 h-5 text-amber-500" />
              {counterFor ? 'Send a Counter Offer' : 'Make an Offer'}
            </DialogTitle>
            <DialogDescription>
              {counterFor
                ? `Responding to GHS ${counterFor.offer_amount?.toLocaleString?.() || counterFor.offer_amount}. Suggest a new price.`
                : 'Propose your price. The seller can accept, counter or reject.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitOffer} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="offer-amount">Amount (GHS)</Label>
              <Input
                id="offer-amount"
                type="number"
                min="1"
                step="1"
                value={offerForm.amount}
                onChange={(e) => setOfferForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="e.g. 80000"
                className="font-mono-display h-12 text-lg"
                data-testid="offer-amount-input"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="offer-note">Note (optional)</Label>
              <Textarea
                id="offer-note"
                value={offerForm.note}
                onChange={(e) => setOfferForm(f => ({ ...f, note: e.target.value }))}
                placeholder="Add a short message..."
                rows={3}
                data-testid="offer-note-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => { setShowOfferModal(false); setCounterFor(null); }}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingOffer}
                className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
                data-testid="submit-offer-btn"
              >
                <HandCoins className="w-4 h-4" />
                {submittingOffer ? 'Sending…' : (counterFor ? 'Send Counter' : 'Send Offer')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
